import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { addVisitorOnKiosk } from '../data/kiosk.duck';

interface Props {
  onClose: () => void;
}

export function AddVisitorPanel({ onClose }: Props) {
  const dispatch = useDispatch();
  const [visitorName, setVisitorName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [signInOnArrival, setSignInOnArrival] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!visitorName.trim()) {
      setError('Visitor name is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    const now = new Date();
    try {
      const result = await dispatch(addVisitorOnKiosk({
        request: {
          visitorName: visitorName.trim(),
          companyName: companyName.trim(),
          email: email.trim() || undefined,
          dueInHours: now.getHours(),
          dueInMinutes: now.getMinutes(),
          signInOnArrival,
        },
      }));
      if (result && (result as any).type?.endsWith('/rejected')) {
        setError((result as any).payload || 'Could not add visitor');
        return;
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel>
      <Header>
        <BodyLarge weight={600}>Add a new visitor</BodyLarge>
        <CloseButton type="button" onClick={onClose} disabled={submitting}>Cancel</CloseButton>
      </Header>
      <Field>
        <BodyRegular>Visitor name</BodyRegular>
        <Input
          value={visitorName}
          onChange={e => setVisitorName(e.target.value)}
          maxLength={120}
          autoFocus
          disabled={submitting}
        />
      </Field>
      <Field>
        <BodyRegular>Company (optional)</BodyRegular>
        <Input
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          maxLength={120}
          disabled={submitting}
        />
      </Field>
      <Field>
        <BodyRegular>Email (optional)</BodyRegular>
        <Input
          value={email}
          onChange={e => setEmail(e.target.value)}
          type="email"
          maxLength={255}
          disabled={submitting}
        />
      </Field>
      <CheckboxRow>
        <input
          id="sign-in-on-arrival"
          type="checkbox"
          checked={signInOnArrival}
          onChange={e => setSignInOnArrival(e.target.checked)}
          disabled={submitting}
        />
        <label htmlFor="sign-in-on-arrival"><BodyRegular>Sign them in now</BodyRegular></label>
      </CheckboxRow>
      {error && <ErrorBox>{error}</ErrorBox>}
      <SubmitButton type="button" onClick={submit} disabled={submitting}>
        {submitting ? 'Adding…' : 'Add visitor'}
      </SubmitButton>
    </Panel>
  );
}

const Panel = styled.div`
  background: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Input = styled.input`
  height: 52px;
  padding: 0 14px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 8px;
  font-size: 18px;
  background: white;
  &:disabled { background: ${Colours.veryLightGrey}; }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  input { width: 20px; height: 20px; }
`;

const SubmitButton = styled.button`
  height: 64px;
  border-radius: 8px;
  border: none;
  background: ${Colours.blue};
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${Colours.darkGrey};
  font-size: 15px;
  cursor: pointer;
  padding: 8px 12px;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ErrorBox = styled.div`
  padding: 12px 14px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 8px;
`;
